import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getCurrentUser, signIn, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

const AuthContext = createContext();
const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const inactivityTimerRef = useRef(null);

  // Reset inactivity timer
  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    if (isAuthenticated) {
      inactivityTimerRef.current = setTimeout(() => {
        handleSignOut();
      }, INACTIVITY_TIMEOUT);
    }
  };

  useEffect(() => {
    // Don't auto-check auth if user just signed out
    const justSignedOut = sessionStorage.getItem('justSignedOut');
    if (justSignedOut) {
      sessionStorage.removeItem('justSignedOut');
      setLoading(false);
      return;
    }

    checkAuthState();

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      
      if (event === 'signedIn') {
        checkAuthState();
      } else if (event === 'signedOut') {
        setUser(null);
        setIsAuthenticated(false);
      }
    });

    return unsubscribe;
  }, []);

  // Set up inactivity tracking
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, resetInactivityTimer);
    });

    resetInactivityTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isAuthenticated]);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      
      // Try to get session, but don't fail if Identity Pool credentials aren't available
      let session;
      try {
        session = await fetchAuthSession();
      } catch (sessionError) {
        console.warn('Could not fetch auth session (Identity Pool may not be configured):', sessionError);
        session = { tokens: null };
      }
      
      // Use the sub (subject) from the token as userId to match DynamoDB records
      const userId = session.tokens?.idToken?.payload?.sub || currentUser.userId;
      
      setUser({
        userId: userId,
        username: currentUser.username,
        attributes: currentUser.attributes || {},
      });
      setIsAuthenticated(true);
    } catch (err) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (username, password) => {
    try {
      await signIn({ username, password });
      await checkAuthState();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleSignOut = async () => {
    try {
      // Clear inactivity timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      
      // Set flag BEFORE clearing storage
      sessionStorage.setItem('justSignedOut', 'true');
      
      // Sign out from Cognito
      await signOut({ global: true });
      
      // Clear local state
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear browser storage (except the flag)
      const flag = sessionStorage.getItem('justSignedOut');
      localStorage.clear();
      sessionStorage.clear();
      sessionStorage.setItem('justSignedOut', flag);
      
      // Wait a bit to ensure sign-out completes
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Redirect to home page
      window.location.href = '/';
      return { success: true };
    } catch (err) {
      console.error('Sign out error:', err);
      // Even if there's an error, clear local state and redirect
      sessionStorage.setItem('justSignedOut', 'true');
      setUser(null);
      setIsAuthenticated(false);
      localStorage.clear();
      window.location.href = '/';
      return { success: false, error: err.message };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    signIn: handleSignIn,
    signOut: handleSignOut,
    checkAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
